import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, ArrowLeft } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function StoryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/stories/${id}`);
        setStory(data);
      } catch {
        toast.error("Story not found.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const onRelate = async () => {
    if (!user) { toast.message("Sign in to leave an I Relate."); return; }
    try {
      const { data } = await api.post(`/stories/${id}/relate`);
      setStory((s) => ({ ...s, ...data }));
    } catch {
      toast.error("Could not register relate.");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-20 text-mindora-muted">Reading…</div>;
  if (!story) return <div className="max-w-3xl mx-auto px-6 py-20">Story not found.</div>;

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-0 py-20" data-testid="page-story-detail">
      <Link to="/feed" className="inline-flex items-center gap-2 text-sm text-mindora-muted hover:text-mindora-red mb-8" data-testid="story-detail-back">
        <ArrowLeft size={14} /> back to the feed
      </Link>

      <Badge variant="outline" className="bg-transparent border-mindora-red text-mindora-red rounded-full px-3 py-0.5 text-[10px] uppercase tracking-[0.2em]">
        {story.tag}
      </Badge>

      <h1 className="font-serif text-4xl lg:text-5xl text-mindora-ink leading-tight mt-5">
        {story.title}
      </h1>

      <div className="mt-3 text-sm text-mindora-muted">
        — {story.author_name} · {new Date(story.created_at).toLocaleDateString()}
      </div>

      <div className="divider-vine my-10" />

      <p className="font-serif text-xl leading-relaxed text-mindora-ink whitespace-pre-wrap" data-testid="story-detail-body">
        {story.body}
      </p>

      <div className="divider-vine my-12" />

      <div className="text-center">
        <button
          onClick={onRelate}
          data-testid="story-detail-relate-button"
          className={`inline-flex items-center gap-3 rounded-full border px-8 py-4 text-base transition-all ${
            story.i_related
              ? "bg-mindora-red text-mindora-bg border-mindora-red"
              : "bg-transparent text-mindora-ink border-mindora-line hover:border-mindora-red hover:text-mindora-red"
          }`}
        >
          <Heart size={18} fill={story.i_related ? "#FDFBF7" : "none"} className={story.i_related ? "animate-soft-pulse" : ""} />
          I Relate · {story.relate_count}
        </button>
        <p className="mt-3 text-xs text-mindora-muted italic">no comments, no replies — just one quiet acknowledgment</p>
      </div>
    </article>
  );
}
